import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import { useRouter } from 'next/router';
import { Button, Chip } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Image from 'next/image';
import groupBy from 'lodash/groupBy';
import clsx from 'clsx';

import toast from 'utils/toast';
import { formatLargeNumber, formatPrice, hasFollowers, ONLYFANS_NETWORK_ID } from 'utils/helpers';
import {
  addInventories,
  openInventoryModal,
  selectInfluencerNetwork,
} from 'features/influencerSetupSlice';
import { setProfile, toggleInfluencerNetworkActive } from 'features/profilesSlice';
import { getRequest, postRequest, putRequest } from 'utils/api';
import { closeModal, openModal } from 'features/utilsSlice';
import { updateConnection } from 'features/onlyFansConnectionsSlice';
import useCurrentProfile from 'hooks/useCurrentProfile';
import useProfileOwner from 'hooks/useProfileOwner';
import OrderIcon from 'public/icons/profile/order.svg';
import EditIcon from 'public/icons/edit-grey.svg';

import OrderDialog from '../orderDialog/OrderDialog';
import PlusIcon from 'public/icons/add-circle-dark.svg';
import EnableIcon from 'public/icons/eye-enable.svg';
import DisableIcon from 'public/icons/eye-disable.svg';
import BasketIcon from 'public/icons/basket-dark.svg';
import GreenEditIcon from 'public/icons/green-edit.svg';
import CloseIcon from 'public/icons/close-dark.svg';

import InventoryModal from 'components/influencerSetup/InventoryModal';
import DropdownMenu from 'components/DropdownMenu';
import WhichAreYouDialog from 'components/signUp/WhichAreYouDialog';
import FullScreenDialog from 'components/FullScreenDialog';
import InventoryEditDialog from 'components/InventoryEditDialog';
import SignatureCameraModal from 'components/influencerSetup/SignatureCameraModal';
import VerificationAccordion from 'components/profileVerification/VerificationAccordion';
import IconMainContainer from 'components/IconMainContainer';
import ConfirmationDialog from 'components/ConfirmationDialog';
import CenteredDialog from 'components/CenteredDialog';
import ReadMore from 'components/ReadMore';
import styles from 'styles/components/profile/Inventory.module.scss';
import InfluencerC4VerificationDialog from 'components/onlyFansC4Verification/InfluencerC4VerificationDialog';

const greyIconStyle = {
  filter: 'invert(100%) sepia(8%) saturate(123%) hue-rotate(188deg) brightness(70%) contrast(90%)',
};

const disableIconStyle = {
  filter: 'invert(25%) sepia(8%) saturate(123%) hue-rotate(188deg) brightness(76%) contrast(90%)',
};

const getDefaultPrice = inventory => {
  const defaultItem = inventory.inventory_items
    .filter(item => item.active)
    .find(item => item.default);

  const item = defaultItem || inventory.inventory_items.filter(item => item.active)[0];

  if (!item) return 'N/A';

  if (inventory.inventory_space.guaranteed_gains) {
    const pricePerSub = +item.price / item.live_time_duration;

    return `$${formatLargeNumber(pricePerSub)} per sub`;
  }

  if (+item.min_offer_price > 0 && +item.max_offer_price > 0) {
    return `$${formatLargeNumber(item.min_offer_price)} - $${formatLargeNumber(
      item.max_offer_price
    )}`;
  }

  return formatPrice(item.price);
};

const btnStyle = { height: 30, padding: '0 5px 0 10px' };
const followersStyle = {
  fontFamily: 'Avenir Heavy',
  fontSize: 15,
  color: '#fff',
};

const getInfluencerNetwork = inventories => inventories?.[0]?.influencer_network;

const Inventory = ({
  selectedNetworkIds: selectedNetworkIdsProp,
  inventories,
  profile,
  profilePreview,
  isDialog,
  fan,
}) => {
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  const [selectedInventoryNetwork, setSelectedInventoryNetwork] = useState({});
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const selectedNetworkIds = useSelector(state => state.filterInventory.selectedNetworkIds);
  const selectedSpaces = useSelector(state => state.filterInventory.selectedSpaces);
  const cartItems = useSelector(state => state.cart.cart_items);
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const profileOwner = useProfileOwner(profile);
  const currentProfile = useCurrentProfile();

  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const smallDesktop = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const dispatch = useDispatch();
  const { username } = router.query;
  const fromDiscover = router.pathname.includes('/discover');

  const isOwnProfilePage = username === currentProfile?.username;
  const unverifiedNetwork = selectedInventoryNetwork?.status === 'unverified';

  const groupedInventories = groupBy(
    [...inventories].sort((a, b) => b.influencer_network.active - a.influencer_network.active),
    inventory => inventory.influencer_network.network.id
  );

  const sortInventories = () => {
    if (profile.adult) {
      return Object.values(groupedInventories).sort((a, b) => {
        const hasOnlyFans = item => item[0].influencer_network.network_id === ONLYFANS_NETWORK_ID;
        const hasTwitter = item => item[0].influencer_network.network_id === 4;
        const hasInstagram = item => item[0].influencer_network.network_id === 1;

        if (hasOnlyFans(a) && !hasOnlyFans(b)) {
          return -1;
        } else if (!hasOnlyFans(a) && hasOnlyFans(b)) {
          return 1;
        } else if (hasTwitter(a) && !hasTwitter(b)) {
          return -1;
        } else if (!hasTwitter(a) && hasTwitter(b)) {
          return 1;
        } else if (hasInstagram(a) && !hasInstagram(b)) {
          return -1;
        } else if (!hasInstagram(a) && hasInstagram(b)) {
          return 1;
        } else {
          return 0;
        }
      });
    } else
      return Object.values(groupedInventories).sort(
        (a, b) => b[0].influencer_network.active - a[0].influencer_network.active
      );
  };

  const sortFilteredInventories = () =>
    Object.values(groupedInventories)
      .filter(arr => arr[0].influencer_network.active)
      .sort((a, b) => {
        if (typeof selectedNetworkIdsProp === 'number')
          // Sort by selected network
          return (
            (a[0].influencer_network?.network_id < selectedNetworkIdsProp) -
            (b[0].influencer_network?.network_id < selectedNetworkIdsProp)
          );

        if (!selectedNetworkIdsProp) return 0;

        if (selectedNetworkIdsProp.length === 0) return 0;

        const aIsSelected = selectedNetworkIdsProp.includes(a[0].influencer_network?.network_id);
        const bIsSelected = selectedNetworkIdsProp.includes(b[0].influencer_network?.network_id);

        if (aIsSelected && !bIsSelected) {
          return -1;
        } else if (!aIsSelected && bIsSelected) {
          return 1;
        } else {
          return 0;
        }
      });

  const sortedInventories =
    profileOwner && sortFilteredInventories.length === 0
      ? sortInventories()
      : sortFilteredInventories();

  const filterInventories = inventories => {
    if (selectedNetworkIds.length === 0) return inventories;

    return inventories.filter(
      inventory =>
        selectedNetworkIds.includes(inventory.influencer_network.network_id) &&
        (selectedSpaces.includes(inventory.inventory_space.name) || selectedSpaces.length === 0)
    );
  };

  const fetchInventories = useCallback(() => {
    if (!profile?.id) return;

    getRequest({
      endpoint: 'inventories',
      query: { profile_id: profile.id, per_page: 200 },
    })
      .then(inventories => {
        dispatch(addInventories(inventories));
        dispatch(setProfile({ ...profile, inventories }));
      })
      .catch(() => {});
  }, [dispatch, profile]);

  const inventoryModalData = inventorySpace => {
    const inventory = inventories.find(i => i.inventory_space.id === inventorySpace.id);

    if (inventory) {
      const formattedInventory = {
        ...inventory,
        inventory_items: [...inventory.inventory_items]
          .sort((a, b) => b.default - a.default)
          .map((item, index) => ({ ...item, index })),
      };

      return { inventory: formattedInventory, inventorySpace };
    } else {
      return { inventorySpace };
    }
  };

  const handleClose = () => {
    dispatch(closeModal('Profile Verification'));
    setSelectedInventoryNetwork({});
  };

  const handleDisconnectFromOF = () => {
    postRequest({
      endpoint: `influencer_networks/${selectedInventoryNetwork.id}/disconnect_only_fans`,
      successMessage: 'Successfully disconnected from OnlyFans',
    })
      .then(() => {
        fetchInventories();
        dispatch(
          updateConnection({
            id: selectedInventoryNetwork.only_fans_connection_id,
            influencer_network_id: null,
          })
        );
      })
      .catch(() => {});

    setOpenConfirmation(false);
    setSelectedInventoryNetwork({});
    dispatch(closeModal('Confirmation'));
  };

  const openVerification = () => {
    const onlyFansNetwork = selectedInventoryNetwork.network_id === ONLYFANS_NETWORK_ID;

    if (onlyFansNetwork)
      dispatch(
        openModal({
          name: 'Influencer OnlyFans Verification',
          data: { influencerNetworkId: selectedInventoryNetwork.id },
        })
      );

    if (selectedInventoryNetwork.status === 'unverified' && !onlyFansNetwork) {
      dispatch(
        openModal({
          name: 'Inventory Edit',
          data: {
            step: 'Profile Verification',
            influencerNetworkId: selectedInventoryNetwork.id,
          },
        })
      );

      handleClose();
    } else handleClose();
  };

  const renderInventories = inventories =>
    inventories
      .filter(inventory => (profilePreview ? inventory.influencer_network.active : true))
      .sort((a, b) => b.active - a.active)
      .map((inventory, index) => {
        const description = inventory.description || inventory.inventory_space?.description;
        return (
          <div
            key={index}
            className={clsx(styles.inventoryNetwork, {
              [styles.disabledInventory]:
                !inventory.active ||
                !inventory.influencer_network.active ||
                inventory.inventory_items.every(item => !item.active),
              [styles.noHover]: profilePreview,
            })}
            onClick={() => {
              if (!inventory.active || !inventory.influencer_network.active || profilePreview)
                return;

              if (
                profileOwner &&
                inventory.influencer_network.profile_screenshot_status !== 'verified' &&
                inventory.influencer_network.network?.name !== 'Image & Likeness' &&
                inventory.influencer_network.status !== 'verified'
              ) {
                dispatch(openModal({ name: 'Profile Verification' }));
                setSelectedInventoryNetwork(inventory.influencer_network);
                return;
              }

              if (
                profileOwner &&
                inventory.inventory_space.name === 'Digital Signature' &&
                !inventory?.media_url &&
                mobile
              ) {
                dispatch(openModal({ name: 'Signature Camera', data: { inventory } }));
                return;
              }

              // Prevent models from buying shoutout from regular profiles
              if (!isOwnProfilePage && currentProfile?.adult && !profile.adult) {
                toast('error', 'Unable to add. This account has limited who can buy from them', {
                  icon: (
                    <IconMainContainer
                      src="/icons/notifications/times.svg"
                      alt="Times"
                      errorStyle
                    />
                  ),
                });
                return;
              }

              setSelectedInventory(inventory);

              if (profileOwner) {
                const itemId =
                  inventory?.inventory_items.filter(i => i.active).find(i => i.default)?.id ||
                  inventory?.inventory_items.filter(i => i.active)[0]?.id;

                router.push(
                  {
                    url: router.pathname,
                    query: { username: profile.username, i: inventory.id, ii: itemId },
                  },
                  undefined,
                  {
                    shallow: true,
                  }
                );
              }

              const cartItem = cartItems.find(cartItem => cartItem.inventory_id === inventory.id);

              if (cartItem) setSelectedCartItem(cartItem);
            }}
          >
            <div>
              <div style={{ minWidth: 30 }}>
                <Image
                  src={inventory.influencer_network.network.active_icon_url}
                  width={30}
                  height={30}
                  alt={inventory.influencer_network.network.name}
                  unoptimized
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'baseline',
                  paddingRight: 20,
                  color: '#323643',
                }}
              >
                <div className={styles.space}>
                  <h2 style={{ fontSize: 14 }}>{inventory.inventory_space.name}</h2>

                  {profileOwner && inventory.inventory_space.fan && (
                    <div style={{ marginBottom: 3 }}>
                      <Chip
                        className="light-purple-chip"
                        label="Fan Inventory"
                        style={{ display: 'inline', marginLeft: 5, marginRight: 0 }}
                        classes={{ label: 'mr-0' }}
                      />
                    </div>
                  )}
                </div>

                <ReadMore
                  mobileLength={90}
                  desktopLength={200}
                  description={description}
                  style={{ fontSize: 12 }}
                />
              </div>
            </div>

            <div>
              {inventory.active && inventory.influencer_network.active ? (
                <span className={styles.price}>{getDefaultPrice(inventory)}</span>
              ) : (
                <span className="disabled-chip">disabled</span>
              )}

              {(profilePreview || !profileOwner) && (
                <div className={profilePreview ? styles.svgNoHover : {}}>
                  <OrderIcon width={42} height={42} alt="Order" />
                </div>
              )}

              {profileOwner && !profilePreview && (
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (
                      profileOwner &&
                      inventory.inventory_space.name === 'Digital Signature' &&
                      !inventory?.media_url &&
                      mobile
                    ) {
                      dispatch(openModal({ name: 'Signature Camera', data: { inventory } }));
                      return;
                    }

                    if (
                      profileOwner &&
                      inventory.influencer_network.profile_screenshot_status !== 'verified' &&
                      inventory.influencer_network.network?.name !== 'Image & Likeness' &&
                      inventory.influencer_network.status !== 'verified'
                    ) {
                      dispatch(openModal({ name: 'Profile Verification' }));
                      setSelectedInventoryNetwork(inventory.influencer_network);
                      return;
                    }

                    dispatch(selectInfluencerNetwork(inventory.influencer_network));

                    dispatch(openInventoryModal(inventoryModalData(inventory.inventory_space)));
                  }}
                >
                  <EditIcon />
                </div>
              )}
            </div>
          </div>
        );
      });

  useEffect(() => {
    if (!isDialog || cartItems.length === 0) return;

    dispatch(openModal({ name: 'Cart' }));
  }, [isDialog, dispatch, cartItems.length]);

  useEffect(() => {
    if (!router.query.ii) return;

    const inventoryId = router.query.i;

    setSelectedInventory({ id: +inventoryId });

    const cartItem = cartItems.find(cartItem => cartItem.inventory_id === +inventoryId);

    if (cartItem) setSelectedCartItem(cartItem);
  }, [isLoggedIn, profile.username, router, cartItems]);

  const handleToggleActive = influencerNetwork => {
    putRequest({
      endpoint: `influencer_networks/${influencerNetwork.id}`,
      data: { influencer_network: { active: !influencerNetwork.active } },
    })
      .then(response => {
        dispatch(
          toggleInfluencerNetworkActive({
            id: influencerNetwork.id,
            username: profile.username_lower,
            active: response.data.active,
          })
        );
      })
      .catch(() => {});
  };

  const settingsItemsForNetwork = influencerNetwork => {
    let settings = [
      {
        name: 'Manage inventory',
        icon: <BasketIcon width={24} height={24} />,
        handleClick: () => {
          dispatch(selectInfluencerNetwork(influencerNetwork));
          dispatch(
            openModal({
              name: 'Inventory Edit',
              data: { step: 'Inventory', fanOnly: fan },
            })
          );
        },
      },
    ];

    if (influencerNetwork.active) {
      settings.push({
        name: 'Disable network',
        icon: (
          <DisableIcon
            className={styles.darkIcon}
            width={24}
            height={24}
            style={disableIconStyle}
          />
        ),
        handleClick: () => handleToggleActive(influencerNetwork),
      });
    } else {
      settings.push({
        name: 'Enable network',
        icon: <EnableIcon className={styles.darkIcon} width={24} height={24} />,
        handleClick: () => handleToggleActive(influencerNetwork),
      });
    }

    if (sortedInventories.length < 12) {
      // 12 is the max number of networks
      settings.push({
        name: 'Add a new network',
        icon: <PlusIcon width={24} height={24} />,
        handleClick: () => {
          dispatch(openModal({ name: 'Inventory Edit', data: { step: 'Networks' } }));
        },
      });
    }

    if (
      influencerNetwork.status === 'verified' &&
      influencerNetwork.network.name === 'OnlyFans' &&
      !influencerNetwork.only_fans_connection_id
    ) {
      settings.push({
        name: 'Connect with OnlyFans',
        icon: <EditIcon width={24} height={24} style={greyIconStyle} />,
        handleClick: () => {
          dispatch(
            openModal({
              name: 'Influencer OnlyFans Verification',
              data: { influencerNetworkId: influencerNetwork.id },
            })
          );
        },
      });
    }

    if (
      influencerNetwork.status === 'verified' &&
      influencerNetwork.network.name === 'OnlyFans' &&
      influencerNetwork.only_fans_connection_id
    ) {
      settings.push({
        name: 'Disconnect from OnlyFans',
        icon: <CloseIcon width={24} height={24} />,
        handleClick: () => {
          setOpenConfirmation(true);
          setSelectedInventoryNetwork(influencerNetwork);
          dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
        },
      });
    }

    if (
      ['pending_verification', 'verified'].includes(influencerNetwork.status) &&
      influencerNetwork.network.name !== 'OnlyFans'
    ) {
      settings.push({
        name: 'Update screenshots',
        icon: <EditIcon width={24} height={24} style={greyIconStyle} />,
        handleClick: () => {
          dispatch(
            openModal({
              name: 'Inventory Edit',
              data: {
                step: 'Profile Verification',
                influencerNetworkId: influencerNetwork.id,
              },
            })
          );
        },
      });
    }

    return settings;
  };

  return (
    <div>
      <div
        className={styles.inventoryContainer}
        style={desktop && cartItems.length > 0 ? { paddingBottom: 50 } : {}}
      >
        {!profileOwner && <h4>{fan ? '' : 'Inventory'}</h4>}

        {sortedInventories
          .filter(
            inventories =>
              selectedNetworkIds.includes(getInfluencerNetwork(inventories)?.network_id) ||
              selectedNetworkIds.length === 0
          )
          .map(inventories => (
            <div key={getInfluencerNetwork(inventories)?.network_id} className={styles.inventory}>
              {filterInventories(inventories).some(
                i =>
                  i.influencer_network.network_id === getInfluencerNetwork(inventories)?.network_id
              ) && (
                <>
                  <div className={styles.networkHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <h5>
                        {fan &&
                        getInfluencerNetwork(inventories)?.network.name === 'Image & Likeness'
                          ? 'FAN DEALS'
                          : getInfluencerNetwork(inventories)?.network.name.toUpperCase()}
                      </h5>

                      {profileOwner &&
                        !profilePreview &&
                        hasFollowers(getInfluencerNetwork(inventories)) && (
                          <DropdownMenu
                            menuItems={settingsItemsForNetwork(getInfluencerNetwork(inventories))}
                            divider={false}
                            hasBtnStyle
                            menuTitle={
                              <Button
                                component="div"
                                variant="contained"
                                color="primary"
                                style={btnStyle}
                                endIcon={<GreenEditIcon />}
                                className={styles.button}
                              >
                                <>
                                  <span style={followersStyle}>
                                    {formatLargeNumber(
                                      getInfluencerNetwork(inventories).max_followers
                                    )}
                                  </span>

                                  {getInfluencerNetwork(inventories).top_creator_percentage > 0 && (
                                    <span style={followersStyle}>
                                      <span style={{ margin: '0 5px' }}>-</span>Top{' '}
                                      {getInfluencerNetwork(inventories).top_creator_percentage}%
                                    </span>
                                  )}
                                </>
                              </Button>
                            }
                          />
                        )}

                      {!profileOwner && hasFollowers(getInfluencerNetwork(inventories)) && (
                        <div className={styles.followers}>
                          <span>
                            {formatLargeNumber(getInfluencerNetwork(inventories).max_followers)}
                          </span>

                          {getInfluencerNetwork(inventories).top_creator_percentage > 0 && (
                            <span>
                              <span style={{ margin: '0 5px' }}>-</span>Top{' '}
                              {getInfluencerNetwork(inventories).top_creator_percentage}%
                            </span>
                          )}
                        </div>
                      )}

                      {getInfluencerNetwork(inventories).network_id === ONLYFANS_NETWORK_ID &&
                        getInfluencerNetwork(inventories).only_fans_connection_id && (
                          <div
                            style={
                              smallDesktop
                                ? { width: '100%', order: '-1' }
                                : { width: 'auto', order: 0, marginLeft: 10 }
                            }
                          >
                            <Chip label="OnlyFans connected" className="blue-chip" />
                          </div>
                        )}
                    </div>

                    {!getInfluencerNetwork(inventories).active && !profilePreview && (
                      <span className="disabled-chip" style={{ marginLeft: 'auto' }}>
                        network disabled
                      </span>
                    )}

                    {profileOwner && !profilePreview && (
                      <DropdownMenu
                        iconSrc="/icons/dots.svg"
                        alt="Dots"
                        menuItems={settingsItemsForNetwork(getInfluencerNetwork(inventories))}
                        divider={false}
                      />
                    )}
                  </div>

                  {profileOwner &&
                    !profilePreview &&
                    ['unverified', 'rejected'].includes(
                      getInfluencerNetwork(inventories).status
                    ) && (
                      <VerificationAccordion
                        influencerNetwork={getInfluencerNetwork(inventories)}
                        styles={styles}
                        automaticVerification={
                          getInfluencerNetwork(inventories).network.automatic_verification
                        }
                      />
                    )}
                </>
              )}

              {renderInventories(filterInventories(inventories))}
            </div>
          ))}
      </div>

      <InventoryModal fetchInventories={fetchInventories} profile={profile} />

      <SignatureCameraModal fetchInventories={fetchInventories} />

      <CenteredDialog
        modalName="Profile Verification"
        title={
          unverifiedNetwork
            ? 'You need to verify your network before editing the price'
            : 'Please wait for your network to be approved'
        }
        textarea={false}
        handleSubmit={openVerification}
        submitText={unverifiedNetwork ? 'Verify' : 'Ok'}
        handleDialogClose={handleClose}
        newBtnStyles={{ marginTop: 20 }}
        alwaysEnabled
      />

      <OrderDialog
        inventoryId={selectedInventory?.id}
        cartItem={selectedCartItem}
        open={!!selectedInventory}
        handleClose={() => {
          if (router.query.i) {
            router.replace(window.location.pathname, undefined, { shallow: true }).then(() => {
              setSelectedInventory(null);
              setSelectedCartItem(null);
            });
          } else {
            setSelectedInventory(null);
            setSelectedCartItem(null);
          }
        }}
        username={profile.username}
        displayName={profile.display_name}
        profileOwner={profileOwner}
        fromProfile={username ? true : false}
        fromDiscover={fromDiscover}
        fromInventory
      />

      {profileOwner && !profilePreview && (
        <>
          <InventoryEditDialog fetchInventories={fetchInventories} />

          <InfluencerC4VerificationDialog fetchInventories={fetchInventories} />
        </>
      )}

      {isLoggedIn ? (
        <FullScreenDialog
          title="GREAT JOB!"
          subtitle="You’ve purchased a gig!"
          btnText="My Dashboard"
          handleClick={() => router.push('/dashboard')}
          iconSrc="/icons/green-right-arrow.svg"
          alt="Green Arrow"
        />
      ) : (
        <WhichAreYouDialog />
      )}

      {openConfirmation && (
        <ConfirmationDialog
          title="Are you sure? This will also disable OF automation when acting as a buyer."
          handleApprove={handleDisconnectFromOF}
        />
      )}
    </div>
  );
};

Inventory.propTypes = {
  inventories: PropTypes.array.isRequired,
  profile: PropTypes.object.isRequired,
  profilePreview: PropTypes.bool,
  selectedNetworkIds: PropTypes.array,
  fan: PropTypes.bool,
};

Inventory.defaultProps = {
  profilePreview: false,
  selectedNetworkIds: [],
  fan: false,
};

Inventory.whyDidYouRender = true;

export default Inventory;
